import { ObservationTimelineEntryType } from '@/api/generated/api.enum';
import type { Components } from '@/api/generated/api';
import {
  addMaterialsToSupportMeasureEntry,
  addCompetencesToSupportMeasureEntry,
  addStudentsToSupportMeasureEntry,
  createSupportMeasureEntry,
  createSupportMeasureProtocolEntry,
  deleteSupportMeasureEntry,
  deleteSupportMeasureProtocolEntry,
  fetchSupportMeasureEntry,
  removeMaterialFromSupportMeasureEntry,
  removeCompetenceFromSupportMeasureEntry,
  removeStudentFromSupportMeasureEntry,
  removeStudentFinallyFromSupportMeasureEntry,
  setSupportMeasureEntryMaterials,
  setSupportMeasureEntryCompetences,
  setSupportMeasureEntryStudents,
  setSupportMeasureProtocolEntryRating,
  updateSupportMeasureEntry,
  updateSupportMeasureProtocolEntry,
} from '@/api/resources/supportMeasureEntry.api';
import { useToast } from '@/composables/useToast';
import { useAnalytics } from '@/composables/analytics/useAnalytics';
import { AnalyticsEvents } from '@/services/analytics/events';
import { useQueryClient } from '@tanstack/vue-query';
import { now } from 'common/utils/date.utils';
import {
  EObservationTimelineChange,
  invalidateObservationTimelineQueries,
  type IObservationTimelineChange,
} from '../observationTimeline/observationTimelineQueryInvalidation';
import { supportMeasureEntryKeys } from './supportMeasureEntry.keys';
import {
  invalidateSupportMeasureAttachmentPermissions,
  invalidateSupportMeasureEntryQueries,
} from './supportMeasureEntryQueryInvalidation';
import { invalidateGrowthCtaQueries } from '../growth/growthCtaQueryInvalidation';
import { useRefreshSetupChecklist } from '../setupChecklist/useRefreshSetupChecklist';

export type SupportMeasureEntry = Components.Schemas.SupportMeasureEntryDto;
export type SupportMeasureEntryActivity = Components.Schemas.SupportMeasureEntryActivityDto;
export type SupportMeasureProtocolEntry = Components.Schemas.SupportMeasureProtocolEntryDto;
export type CreateSupportMeasureEntryInput = Components.Schemas.CreateSupportMeasureEntryDto;
export type UpdateSupportMeasureEntryInput = Components.Schemas.UpdateSupportMeasureEntryDto;
export type CreateProtocolEntryInput = Components.Schemas.CreateSupportMeasureProtocolEntryDto;
export type UpdateProtocolEntryInput = Components.Schemas.UpdateSupportMeasureProtocolEntryDto;

export enum ESupportMeasureCreationSource {
  Composer = 'composer',
}

interface ICreateSupportMeasureOptions {
  creationSource?: ESupportMeasureCreationSource;
}

export interface IMemberChangeFlags {
  added: boolean;
  removed: boolean;
}

interface ISupportMeasureEntryComposable {
  getById: (id: number) => Promise<SupportMeasureEntry | null>;
  create: (dto: CreateSupportMeasureEntryInput, options?: ICreateSupportMeasureOptions) => Promise<SupportMeasureEntry>;
  update: (id: number, dto: UpdateSupportMeasureEntryInput) => Promise<SupportMeasureEntry>;
  complete: (id: number, params?: { note?: string | null; rating?: number | null }) => Promise<SupportMeasureEntry>;
  remove: (id: number) => Promise<void>;
  setStudents: (id: number, studentIds: number[], flags: IMemberChangeFlags) => Promise<SupportMeasureEntry>;
  setCompetences: (id: number, competenceIds: number[], flags: IMemberChangeFlags) => Promise<SupportMeasureEntry>;
  addCompetences: (id: number, competenceIds: number[], options?: { silent?: boolean }) => Promise<SupportMeasureEntry>;
  removeCompetence: (id: number, competenceId: number) => Promise<SupportMeasureEntry>;
  addStudents: (id: number, studentIds: number[]) => Promise<SupportMeasureEntry>;
  removeStudent: (id: number, studentId: number) => Promise<SupportMeasureEntry>;
  removeStudentFinally: (id: number, studentId: number) => Promise<SupportMeasureEntry>;
  setMaterials: (id: number, externalMaterialIds: number[]) => Promise<SupportMeasureEntry>;
  addMaterials: (id: number, externalMaterialIds: number[]) => Promise<SupportMeasureEntry>;
  removeMaterial: (id: number, externalMaterialId: number) => Promise<SupportMeasureEntry>;
  createProtocolEntry: (
    id: number,
    dto: CreateProtocolEntryInput,
  ) => Promise<SupportMeasureEntry & { createdProtocolEntryId: number }>;
  updateProtocolEntry: (protocolEntryId: number, dto: UpdateProtocolEntryInput) => Promise<SupportMeasureEntry>;
  deleteProtocolEntry: (protocolEntryId: number) => Promise<SupportMeasureEntry>;
  setProtocolEntryRating: (
    protocolEntryId: number,
    rating: Components.Schemas.SupportMeasureProtocolEntryRating | null,
  ) => Promise<SupportMeasureEntry>;
}

export const useSupportMeasureEntry = (): ISupportMeasureEntryComposable => {
  const { createSuccessToast, editSuccessToast, deleteSuccessToast, catchError } = useToast();
  const { trackEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { refreshSetupChecklist } = useRefreshSetupChecklist();

  const finalizeEntryMutation = async (
    entry: SupportMeasureEntry,
    options: { timelineAffected?: boolean; timelineChange?: IObservationTimelineChange } = {},
  ): Promise<SupportMeasureEntry> => {
    queryClient.setQueryData(supportMeasureEntryKeys.detail(entry.id), entry);
    await Promise.all([
      invalidateSupportMeasureEntryQueries(queryClient, {
        entryId: entry.id,
      }),
      ...(options.timelineAffected
        ? [invalidateObservationTimelineQueries(queryClient, entry.schoolClassId, options.timelineChange)]
        : []),
    ]);
    return entry;
  };

  const getById = (id: number): Promise<SupportMeasureEntry | null> =>
    catchError(() => fetchSupportMeasureEntry({ id }));

  const create = (
    dto: CreateSupportMeasureEntryInput,
    options: ICreateSupportMeasureOptions = {},
  ): Promise<SupportMeasureEntry> =>
    catchError(async () => {
      const entry = await createSupportMeasureEntry(dto);
      await Promise.all([
        finalizeEntryMutation(entry, { timelineAffected: true }),
        invalidateGrowthCtaQueries(queryClient),
        refreshSetupChecklist(),
      ]);
      createSuccessToast();
      trackEvent(AnalyticsEvents.supportMeasure.entryCreated, {
        supportMeasureCreationSource: options.creationSource ?? ESupportMeasureCreationSource.Composer,
      });
      return entry;
    });

  const update = (id: number, dto: UpdateSupportMeasureEntryInput): Promise<SupportMeasureEntry> =>
    catchError(async () => {
      const entry = await updateSupportMeasureEntry({ id, dto });
      await finalizeEntryMutation(entry, {
        timelineAffected: true,
        timelineChange:
          dto.completedAt === undefined && dto.completionNote === undefined && dto.completionRating === undefined
            ? {
                kind: EObservationTimelineChange.Update,
                entity: { type: ObservationTimelineEntryType.supportMeasureEntry, id },
              }
            : undefined,
      });
      if (dto.completedAt !== undefined) await invalidateSupportMeasureAttachmentPermissions(queryClient, entry);
      editSuccessToast();
      if (dto.completedAt === undefined) trackEvent(AnalyticsEvents.supportMeasure.entryUpdated);
      else trackEvent(AnalyticsEvents.supportMeasure.entryCompleted);
      return entry;
    });

  const complete = (
    id: number,
    params: { note?: string | null; rating?: number | null } = {},
  ): Promise<SupportMeasureEntry> =>
    update(id, {
      completedAt: now().toISOString(),
      completionNote: params.note ?? null,
      completionRating: params.rating ?? null,
    });

  const remove = (id: number): Promise<void> =>
    catchError(async () => {
      const cachedEntry = queryClient.getQueryData<SupportMeasureEntry>(supportMeasureEntryKeys.detail(id));
      await deleteSupportMeasureEntry({ id });
      queryClient.removeQueries({ queryKey: supportMeasureEntryKeys.detail(id) });
      queryClient.removeQueries({ queryKey: supportMeasureEntryKeys.filterStudents(id) });
      await invalidateObservationTimelineQueries(queryClient, cachedEntry?.schoolClassId, {
        kind: EObservationTimelineChange.Delete,
        entity: { type: ObservationTimelineEntryType.supportMeasureEntry, id },
      });
      deleteSuccessToast();
      trackEvent(AnalyticsEvents.supportMeasure.entryDeleted);
    });

  const setStudents = (id: number, studentIds: number[], flags: IMemberChangeFlags): Promise<SupportMeasureEntry> =>
    catchError(async () => {
      const entry = await setSupportMeasureEntryStudents({ id, studentIds });
      await finalizeEntryMutation(entry, { timelineAffected: true });
      editSuccessToast();
      if (flags.added) trackEvent(AnalyticsEvents.supportMeasure.studentsAdded);
      if (flags.removed) trackEvent(AnalyticsEvents.supportMeasure.studentRemoved);
      return entry;
    });

  const setCompetences = (
    id: number,
    competenceIds: number[],
    flags: IMemberChangeFlags,
  ): Promise<SupportMeasureEntry> =>
    catchError(async () => {
      const entry = await setSupportMeasureEntryCompetences({ id, competenceIds });
      await finalizeEntryMutation(entry, { timelineAffected: true });
      editSuccessToast();
      if (flags.added) trackEvent(AnalyticsEvents.supportMeasure.competencesAdded);
      if (flags.removed) trackEvent(AnalyticsEvents.supportMeasure.competenceRemoved);
      return entry;
    });

  const addCompetences = (
    id: number,
    competenceIds: number[],
    options: { silent?: boolean } = {},
  ): Promise<SupportMeasureEntry> =>
    catchError(async () => {
      const entry = await addCompetencesToSupportMeasureEntry({ id, competenceIds });
      await finalizeEntryMutation(entry, { timelineAffected: true });
      // silent when this is a side effect of another action (e.g. connecting picks while submitting a
      // protocol entry) — that action already shows its own toast
      if (!options.silent) editSuccessToast();
      trackEvent(AnalyticsEvents.supportMeasure.competencesAdded);
      return entry;
    });

  const removeCompetence = (id: number, competenceId: number): Promise<SupportMeasureEntry> =>
    catchError(async () => {
      const entry = await removeCompetenceFromSupportMeasureEntry({ id, competenceId });
      await finalizeEntryMutation(entry, { timelineAffected: true });
      editSuccessToast();
      trackEvent(AnalyticsEvents.supportMeasure.competenceRemoved);
      return entry;
    });

  const addStudents = (id: number, studentIds: number[]): Promise<SupportMeasureEntry> =>
    catchError(async () => {
      const entry = await addStudentsToSupportMeasureEntry({ id, studentIds });
      await finalizeEntryMutation(entry, { timelineAffected: true });
      editSuccessToast();
      trackEvent(AnalyticsEvents.supportMeasure.studentsAdded);
      return entry;
    });

  const removeStudent = (id: number, studentId: number): Promise<SupportMeasureEntry> =>
    catchError(async () => {
      const entry = await removeStudentFromSupportMeasureEntry({ id, studentId });
      await finalizeEntryMutation(entry, { timelineAffected: true });
      editSuccessToast();
      trackEvent(AnalyticsEvents.supportMeasure.studentRemoved);
      return entry;
    });

  const removeStudentFinally = (id: number, studentId: number): Promise<SupportMeasureEntry> =>
    catchError(async () => {
      const entry = await removeStudentFinallyFromSupportMeasureEntry({ id, studentId });
      await finalizeEntryMutation(entry, { timelineAffected: true });
      deleteSuccessToast();
      trackEvent(AnalyticsEvents.supportMeasure.studentRemoved);
      return entry;
    });

  const setMaterials = (id: number, externalMaterialIds: number[]): Promise<SupportMeasureEntry> =>
    catchError(async () => {
      const entry = await setSupportMeasureEntryMaterials({ id, externalMaterialIds });
      await finalizeEntryMutation(entry, { timelineAffected: true });
      editSuccessToast();
      return entry;
    });

  const addMaterials = (id: number, externalMaterialIds: number[]): Promise<SupportMeasureEntry> =>
    catchError(async () => {
      const entry = await addMaterialsToSupportMeasureEntry({ id, externalMaterialIds });
      await finalizeEntryMutation(entry, { timelineAffected: true });
      editSuccessToast();
      return entry;
    });

  const removeMaterial = (id: number, externalMaterialId: number): Promise<SupportMeasureEntry> =>
    catchError(async () => {
      const entry = await removeMaterialFromSupportMeasureEntry({ id, externalMaterialId });
      await finalizeEntryMutation(entry, { timelineAffected: true });
      editSuccessToast();
      return entry;
    });

  const createProtocolEntry = (
    id: number,
    dto: CreateProtocolEntryInput,
  ): Promise<SupportMeasureEntry & { createdProtocolEntryId: number }> =>
    catchError(async () => {
      const entry = await createSupportMeasureProtocolEntry({ id, dto });
      await finalizeEntryMutation(entry);
      createSuccessToast();
      trackEvent(AnalyticsEvents.supportMeasure.protocolEntryCreated);
      return entry;
    });

  const updateProtocolEntry = (protocolEntryId: number, dto: UpdateProtocolEntryInput): Promise<SupportMeasureEntry> =>
    catchError(async () => {
      const entry = await updateSupportMeasureProtocolEntry({ protocolEntryId, dto });
      await finalizeEntryMutation(entry);
      editSuccessToast();
      trackEvent(AnalyticsEvents.supportMeasure.protocolEntryUpdated);
      return entry;
    });

  const deleteProtocolEntry = (protocolEntryId: number): Promise<SupportMeasureEntry> =>
    catchError(async () => {
      const entry = await deleteSupportMeasureProtocolEntry({ protocolEntryId });
      await finalizeEntryMutation(entry);
      deleteSuccessToast();
      trackEvent(AnalyticsEvents.supportMeasure.protocolEntryDeleted);
      return entry;
    });

  const setProtocolEntryRating = (
    protocolEntryId: number,
    rating: Components.Schemas.SupportMeasureProtocolEntryRating | null,
  ): Promise<SupportMeasureEntry> =>
    catchError(async () => {
      const entry = await setSupportMeasureProtocolEntryRating({ protocolEntryId, rating });
      await finalizeEntryMutation(entry);
      return entry;
    });

  return {
    getById,
    create,
    update,
    complete,
    remove,
    setStudents,
    setCompetences,
    addCompetences,
    removeCompetence,
    addStudents,
    removeStudent,
    removeStudentFinally,
    setMaterials,
    addMaterials,
    removeMaterial,
    createProtocolEntry,
    updateProtocolEntry,
    deleteProtocolEntry,
    setProtocolEntryRating,
  };
};
